import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, NgIf, NgFor, DatePipe, DecimalPipe, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { MovieDetailsService } from '../../../../core/services/movie/movie.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-movie-facts',
  standalone: true,
  imports: [CommonModule, NgIf, NgFor, DatePipe, DecimalPipe],
  templateUrl: './movie-facts.html',
  styleUrl: './movie-facts.scss'
})
export class MovieFacts implements OnInit, OnDestroy {

  movieDetails: any = null;
  movieId: string = '';
  movieName: string = '';
  private destroy$ = new Subject<void>();

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private movieDetailsService: MovieDetailsService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const slug = params.get('slug');
      this.getMovieDetails(slug);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getMovieDetails(slugParam?: string | null): void {
    const slug = slugParam !== undefined ? slugParam : this.route.snapshot.paramMap.get('slug');

    if (slug) {
      const [id, name] = slug.split('-');
      this.movieId = id;
      this.movieName = name;
    }

    if (this.movieId) {
      this.movieDetailsService.getMovieDetails(parseInt(this.movieId, 10), 'movie').subscribe({
        next: data => {
          this.movieDetails = data;
          if (isPlatformBrowser(this.platformId)) {
            const title = data?.title || data?.original_title || data?.original_name || 'Movie';
            const overview = data?.overview ? ` - ${data.overview}` : '';
            document.title = `${title}${overview}`;
          }
        },
        error: err => {
          console.error('Error fetching movie details:', err);
        }
      });
    }
  }

  getStatusClass(status: string): string {
    if (!status) return 'status-default';
    const s = status.toLowerCase();
    if (s.includes('released')) return 'status-active';
    if (s.includes('post production') || s.includes('in production')) return 'status-production';
    if (s.includes('planned') || s.includes('rumored')) return 'status-planned';
    if (s.includes('canceled') || s.includes('cancelled')) return 'status-canceled';
    return 'status-default';
  }

  getLanguageName(code: string): string {
    if (!code) return '';
    try {
      const displayNames = new Intl.DisplayNames(['en'], { type: 'language' });
      return displayNames.of(code) || code.toUpperCase();
    } catch {
      return code.toUpperCase();
    }
  }

  formatRuntime(minutes: number): string {
    if (!minutes || minutes <= 0) return '—';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }

  formatCurrency(amount: number): string {
    if (!amount || amount <= 0) return '—';
    if (amount >= 1_000_000_000) {
      return `${(amount / 1_000_000_000).toFixed(2).replace(/\.00$/, '')}B`;
    }
    if (amount >= 1_000_000) {
      return `${(amount / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
    }
    if (amount >= 1_000) {
      return `${(amount / 1_000).toFixed(0)}K`;
    }
    return amount.toLocaleString();
  }

  getProfit(revenue: number, budget: number): { amount: number; isProfit: boolean; text: string } | null {
    if (!revenue || !budget || revenue <= 0 || budget <= 0) return null;
    const diff = revenue - budget;
    const isProfit = diff >= 0;
    const sign = isProfit ? '+' : '-';
    return {
      amount: diff,
      isProfit,
      text: `${sign}$${Math.abs(diff).toLocaleString()}`
    };
  }

  onCompanyLogoError(event: Event): void {
    const target = event.target as HTMLElement;
    if (target) {
      target.style.display = 'none';
    }
  }
}

